import {
    MATCH_IDV_PROCESSING_COMPLETE_ROUTE,
    MATCH_IDV_VERIFICATION_ERROR_ROUTE,
} from '@Router/routes';

/**
 * Decides whether a request-MFE mount should run the "stale invite link →
 * participant dashboard" redirect (see {@link maybeRedirectToInviteDashboard}
 * in setupApp.ts, introduced by DS-2569).
 *
 * The redirect is meant only for a *fresh* invite-link visit when the company
 * has the participant portal enabled — it routes the participant to the unified
 * dashboard instead of the legacy single-request flow.
 *
 * It must NOT fire mid-flow. Two cases skip it:
 *  - DS-2701: the participant clicked a task FROM the dashboard (`source=dashboard`).
 *  - PS-955: the participant is returning from an Intellicheck ID capture. After
 *    capture, Intellicheck issues a full-page redirect back to
 *    `match-idv-processing-complete` / `match-idv-verification-error` (built by
 *    the Request BFF's IdentityCaptureFlowBusiness, which does NOT carry
 *    `source=dashboard`). Those routes are reached ONLY as a capture callback,
 *    never as a fresh invite click — so the redirect would wrongly bounce the
 *    participant to the dashboard before `ProcessingCompleteView` can resolve the
 *    outcome and show the retry screen on a failed verification.
 */

/**
 * Route names that are only ever reached as an Intellicheck capture callback
 * (a full-page redirect mid-IDV-flow), never as a fresh invite-link entry.
 * Sourced from the route-name constants in router/routes.ts so a rename there
 * is a compile error here rather than silent drift.
 */
export const FLOW_CONTINUATION_ROUTE_NAMES: ReadonlySet<string> = new Set([
    MATCH_IDV_PROCESSING_COMPLETE_ROUTE,
    MATCH_IDV_VERIFICATION_ERROR_ROUTE,
]);

export const isFlowContinuationRoute = (routeName: string | null | undefined): boolean =>
    !!routeName && FLOW_CONTINUATION_ROUTE_NAMES.has(routeName);

/** The subset of a resolved entry needed to decide whether to skip the redirect. */
export interface InviteRedirectDecisionInput {
    requestType: unknown;
    fromDashboard: boolean;
    isFlowContinuation: boolean;
}

/**
 * Returns true when the invite-dashboard redirect check must be skipped for this
 * entry. Skip when there is no request type (nothing to redirect), when the
 * participant arrived from the dashboard (DS-2701), or when the entry is an
 * Intellicheck capture continuation (PS-955).
 */
export const shouldSkipInviteDashboardRedirect = (entry: InviteRedirectDecisionInput): boolean =>
    !entry.requestType || entry.fromDashboard || entry.isFlowContinuation;
